import { useState, useEffect } from 'react';
import Modal from './EditModal';
import { post, put } from '../lib/api';
import ConditionedSnackbar from './ConditionedSnackbar';
import { StyledGlobalSwitch } from './ConfigurePage.styles';
import { ListItem, ListItemBaseProps, ListItemText, Typography } from '@mui/material';
import t from '../theme';
import { AppConfigDto } from '../lib/models/AppConfigModel';
import { ErrorResponse } from '../lib/dtos/ErrorResponse';
import ValidationErrorResultDto from '../lib/dtos/ValidationErrorResultDto';

export interface ConfigurableItemDto {
    id: string;
    name: string;
    section: string;
    value: string;
    type: string;
    isEditable: boolean;
    isToggable: boolean;
    isActive?: boolean;
}

interface ListItemComponentProps extends ListItemBaseProps {
    dto: ConfigurableItemDto;
    isInner?: boolean;
}

const ListItemComponent = ({ dto, isInner }: ListItemComponentProps) => {
    const { id, name, value, type, isEditable, isToggable, isActive, section } = dto || {};
    const [innerName, setInnerName] = useState(() => (isInner ? value : name));
    const [data, setData] = useState(value);
    const [innerId, setInnerId] = useState(id);
    const [checked, setChecked] = useState(isActive);
    const [message, setMessage] = useState('');

    //Snackbar - region
    const [isErrored, setErrored] = useState(false);
    const [showSnackbar, setShowSnackbar] = useState(false);
    //endregion

    const onItemToggle = async () => {
        try {
            await put(`appconfigs/${id}/toggle`);
            setChecked(!checked);
            setMessage(`${innerName} was succesfully toggled.`);
            setShowSnackbar(true);
            setErrored(false);
        } catch (error) {
            setErrored(true);
            setMessage(`${innerName} was not succesfully toggled.`);
            setShowSnackbar(true);
        }
    };

    const onValueUpdate = async (value: string) => {
        try {
            const r = await post<AppConfigDto | ErrorResponse<ValidationErrorResultDto[]>>(
                `appconfigs/update-value`,
                {
                    id: innerId,
                    value,
                    type,
                    name: innerName,
                    section: section,
                    isEditable: isEditable,
                    isToggable: isToggable,
                    isActive: isActive
                }
            );

            if ('data' in r.data) {
                if (r.data.data.length > 0) {
                    if (r.data.data[0].errorMessage) throw new Error(r.data.data[0].errorMessage);
                }
            }

            setData(typeof r.data === 'object' ? (r.data as AppConfigDto).value : r.data);
            setInnerId(typeof r.data === 'object' ? (r.data as AppConfigDto).id : r.data);
            setErrored(false);
            setMessage(`${innerName} value succesfully saved.`);
            setShowSnackbar(true);
        } catch (error) {
            setErrored(true);
            if (error instanceof Error) {
                setMessage(error.message);
            } else {
                setMessage(`${innerName} value was not succesfully saved.`);
            }
            setShowSnackbar(true);
        }
    };

    useEffect(() => {
        if (isInner) {
            setInnerName(data);
        }
    }, [showSnackbar, isInner, data]);

    return (
        <>
            <ListItem
                key={innerName}
                sx={{
                    backgroundColor: isInner
                        ? t.palette.listItemBackground.shade
                        : t.palette.listItemBackground.main,
                    mb: isInner ? '0.188rem' : 'inherit',
                    pr: isToggable ? '1rem' : '0'
                }}>
                <ListItemText
                    primary={
                        <Typography variant="h4">
                            {name === 'How long did it take?' ? innerName + ' minutes' : innerName}
                        </Typography>
                    }
                />
                {isEditable ? (
                    <Modal
                        formData={{ name: innerName, value: data, type, isInner, section: name }}
                        onValueUpdate={onValueUpdate}
                    />
                ) : null}
                {isToggable ? (
                    <StyledGlobalSwitch
                        edge="end"
                        onChange={onItemToggle}
                        checked={checked}
                        inputProps={{
                            'aria-labelledby': `switch-list-label-${innerName}`
                        }}
                    />
                ) : null}
            </ListItem>
            <ConditionedSnackbar
                open={showSnackbar}
                handleClose={() => setShowSnackbar(false)}
                severity={!isErrored ? 'success' : 'error'}
                message={message}
            />
        </>
    );
};

export default ListItemComponent;
